import { FC, useEffect, useRef, useState } from 'react';
import { Button, Col, Input, InputRef, Modal, Row } from 'antd';
import { TbArrowRight, TbX } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ResourceDraft, useDirtyResourceDraft } from '@/hooks/useResourceDrafts';
import { useSubmitShortcut } from '@/hooks/useSubmitShortcut';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import { LearnMoreFooter, WorkspaceFolderGlyph } from '@/shared';
import { useFolderStore } from '@/stores';
import { Folder, SetStateMethod } from '@/types';

import CreateMoreControl from '../components/CreateMoreControl';
import ResourceCreationModalTitle from '../components/ResourceCreationModalTitle';

interface CreateFolderModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    initialDraft?: ResourceDraft<any> | null;
    onDraftResolved?: () => void;
}

const CreateFolderModal: FC<CreateFolderModalProps> = ({
    open,
    setOpen,
    initialDraft,
    onDraftResolved,
}): JSX.Element => {
    const [input, setInput] = useState('');
    const [visibility, setVisibility] = useState('Private');

    const [createMore, setCreateMore] = useState(false);

    const navigate = useNavigate();

    const { createFolder, folders, loadingFolder: loading } = useFolderStore();
    const createAccess = useWorkspaceResourceCreateAccess('folders', folders?.length ?? 0);

    const resetForm = () => {
        setInput('');
        setVisibility('Private');
    };

    const handleCreateFolder = async () => {
        if (createAccess.disabled) {
            return;
        }

        const payload = {
            name: input,
            visibility,
        } as Partial<Folder>;

        await createFolder(payload).then((f) => {
            markSubmitted();
            resetForm();
            onDraftResolved?.();

            if (createMore === false) {
                setOpen(false);

                navigate(`/folders/${f?.id}`);
            }
        });
    };

    const { token } = useAntDesignTheme();

    useEffect(() => {
        if (open && initialDraft?.values) {
            setInput(initialDraft.values.input ?? '');
            setVisibility(initialDraft.values.visibility ?? 'Private');
        }
    }, [open, initialDraft]);

    const draftValues = {
        input,
        visibility,
    };

    const { dirtyDraftModal, markSubmitted, requestClose } = useDirtyResourceDraft({
        close: () => setOpen(false),
        draftId: initialDraft?.id,
        isDirty: input.trim() !== '' || visibility !== 'Private',
        reset: resetForm,
        resourceLabel: 'Folder',
        resourceType: 'folder',
        title: input,
        values: draftValues,
    });

    useSubmitShortcut(open && !!input.trim() && !loading && !createAccess.disabled, handleCreateFolder, [
        input,
        visibility,
        createMore,
        createAccess.disabled,
    ]);

    // Autofocus
    const titleInputRef = useRef<InputRef>(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => titleInputRef.current?.focus(), 125);
        }
    }, [open]);

    return (
        <>
            {dirtyDraftModal}
            <Modal
                title={
                    <ResourceCreationModalTitle
                        icon={
                            <WorkspaceFolderGlyph variant='create' size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                        }
                        title='Create Folder'
                        visibility={visibility}
                        setVisibility={setVisibility}
                    />
                }
                closable
                closeIcon={<TbX size='1rem' style={{ verticalAlign: '-0.2rem' }} onClick={requestClose} />}
                footer={
                    <Row style={{ margin: '0' }} align={'middle'}>
                        <Col flex='auto' style={{ textAlign: 'left' }}>
                            <Button style={{}} onClick={requestClose}>
                                Cancel
                            </Button>
                        </Col>
                        <Col flex='none' style={{ textAlign: 'right' }}>
                            <CreateMoreControl
                                checked={createMore}
                                disabled={createAccess.disabled}
                                onChange={setCreateMore}
                            />
                            <Button
                                style={{}}
                                type='primary'
                                onClick={handleCreateFolder}
                                loading={loading}
                                disabled={loading || input === '' || createAccess.disabled}
                            >
                                Create
                                <TbArrowRight style={{ verticalAlign: '-0.15rem', marginLeft: '0.25rem' }} />
                            </Button>
                        </Col>
                    </Row>
                }
                open={open}
                destroyOnHidden
                onCancel={requestClose}
                mask={false}
                style={{ minWidth: 'calc(100vw - 90rem)' }}
            >
                <div style={{ marginBottom: '0.75rem' }}>
                    <small style={{ color: token.colorTextTertiary }}>Name</small>
                </div>
                <Row gutter={[8, 0]}>
                    <Col flex='auto'>
                        <Input
                            ref={titleInputRef}
                            prefix={
                                <WorkspaceFolderGlyph
                                    variant='default'
                                    size='1rem'
                                    style={{ marginRight: '0.375rem' }}
                                />
                            }
                            value={input}
                            onChange={(e) => {
                                setInput(e.target.value);
                            }}
                            style={{ padding: '0.25rem 0.5rem' }}
                            placeholder='European Q3 Investments...'
                            allowClear
                            onPressEnter={() => {
                                if (input.trim() && !loading && !createAccess.disabled) {
                                    handleCreateFolder();
                                }
                            }}
                        />
                    </Col>
                </Row>
                <div style={{ marginTop: '0.75rem' }}>
                    <LearnMoreFooter align='left' topic='Folders' />
                </div>
            </Modal>
        </>
    );
};

export default CreateFolderModal;
